import React, { Suspense } from 'react';
import { isChunkLoadError } from './ErrorBoundary';

const RELOAD_FLAG = 'sd:chunkReloadAt';
const RELOAD_WINDOW_MS = 30000;

function hasRecentlyReloaded() {
  try {
    const ts = Number(window.sessionStorage.getItem(RELOAD_FLAG) || 0);
    return ts > 0 && Date.now() - ts < RELOAD_WINDOW_MS;
  } catch (e) {
    return false;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch (e) {
    /* ignore — private mode / storage disabled */
  }
}

function clearReloadFlag() {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch (e) {
    /* ignore */
  }
}

/**
 * Wrap a route's dynamic import so a stale-cache ChunkLoadError (common right
 * after a deploy, when the old hashed chunks are gone) triggers a single hard
 * reload to fetch the fresh shell + chunks, instead of crashing to a blank page.
 * A sessionStorage guard prevents reload loops if the chunk is genuinely broken;
 * in that case the error propagates to the top-level ErrorBoundary.
 */
function importWithReload(importFn) {
  return importFn().then(
    (mod) => {
      clearReloadFlag();
      return mod;
    },
    (error) => {
      if (isChunkLoadError(error) && !hasRecentlyReloaded()) {
        markReloaded();
        window.location.reload();
        // Keep Suspense in its loading state until the reload takes over.
        return new Promise(() => {});
      }
      throw error;
    },
  );
}

export default function RouteLoadFallback({ label = 'Loading…' }) {
  return (
    <div className="route-load-fallback" role="status" aria-live="polite">
      <div className="route-load-fallback__spinner" aria-hidden />
      <span>{label}</span>
    </div>
  );
}

export function lazyRoute(importFn, label) {
  const Lazy = React.lazy(() => importWithReload(importFn));
  return function LazyRoute(props) {
    return (
      <Suspense fallback={<RouteLoadFallback label={label} />}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}
